import type { ReactNode } from 'react'

/**
 * The customer half of the app, and a different animal from a station screen.
 *
 * No scan bar: a parent browsing on a sofa has no scanner, and mounting one would
 * steal the bottom of the screen from the only control that matters here. What
 * takes that space instead is the cart summary - the persistent, always-reachable
 * thing in the thumb arc (ui-design-system.md 10.2), with the pay/checkout action
 * on the right where a right-handed thumb rests.
 *
 * Credit is shown in the header on every screen rather than buried in an account
 * page, because a customer who has forgotten they hold credit will not go looking
 * for it and Mitali ends up paying out cash she already owed in stock.
 */
export interface CustomerShellProps {
  title: string
  /** Null when signed out - the shell says so rather than showing a zero. */
  creditPaise: number | null
  cart: { lines: number; payablePaise: number } | null
  primaryAction?: { label: string; onPress?: () => void; disabled?: boolean }
  online?: boolean
  children: ReactNode
}

export function rupees(paise: number): string {
  return `Rs ${(paise / 100).toFixed(2)}`
}

export function CustomerShell({ title, creditPaise, cart, primaryAction, online = true, children }: CustomerShellProps) {
  return (
    <div className="storefront">
      <header className="sf-header">
        <h1 className="t-display">{title}</h1>
        {creditPaise === null ? null : (
          <span className="chip chip-credit" aria-label="credit balance">
            {rupees(creditPaise)} credit
          </span>
        )}
      </header>

      {online ? null : (
        <p className="sync-line sync-offline" aria-live="polite">
          Working offline - what you add is kept on this device
        </p>
      )}

      <main className="sf-body">{children}</main>

      <footer className="sf-footer">
        {cart ? (
          <a className="sf-cart-summary" href="#/cart">
            {cart.lines} in cart - {rupees(cart.payablePaise)}
          </a>
        ) : (
          <span className="sf-cart-summary muted">Cart empty</span>
        )}
        {primaryAction ? (
          <button
            type="button"
            className={`btn btn-primary thumb-primary${primaryAction.disabled ? '' : ' pulse-halo'}`}
            disabled={primaryAction.disabled}
            onClick={primaryAction.onPress}
          >
            {primaryAction.label}
          </button>
        ) : null}
      </footer>
    </div>
  )
}
